/* global npVars */

/**
 * Page Archive AJAX Search
 *
 * WCAG-compliant combobox with focus-trapped dropdown. Uses HTML templates
 * for rendering; no inline HTML strings.
 *
 * @see templates/flexible-content/page_archive.php
 */
class PageArchiveSearch {

    /**
     * Initialize all page archive search instances on the document.
     */
    static setupModules() {
        document.querySelectorAll('[data-page-archive-search]').forEach((wrapper) => {
            new PageArchiveSearch(wrapper).init();
        });
    }

    /**
     * @param {HTMLElement} wrapperEl - The search wrapper element (`[data-page-archive-search]`)
     */
    constructor(wrapperEl) {
        this.wrapper = wrapperEl;
        this.input = wrapperEl.querySelector('.page-archive-search-input');
        this.dropdown = wrapperEl.querySelector('.page-archive-search-dropdown');
        this.spinner = wrapperEl.querySelector('.page-archive-search-spinner') || this.createSpinner();
        this.parentCategory = wrapperEl.getAttribute('data-parent-category') || '0';
        this.tplResult = wrapperEl.querySelector('.page-archive-tpl-result');
        this.tplNoResults = wrapperEl.querySelector('.page-archive-tpl-no-results');
        this.tplError = wrapperEl.querySelector('.page-archive-tpl-error');
        this.selectedIndex = -1;
        this.abortController = null;
        this.lastResults = [];
        this.instId = `pa-${Math.random().toString(36).slice(2, 9)}`;
    }

    /**
     * Creates and inserts the spinner element when not present in the DOM.
     * @returns {HTMLElement}
     */
    createSpinner() {
        const spinner = document.createElement('div');
        spinner.className = 'page-archive-search-spinner';
        spinner.setAttribute('role', 'status');
        spinner.setAttribute('aria-label', 'Loading');
        spinner.setAttribute('aria-hidden', 'true');
        const form = this.wrapper.querySelector('form');

        if (form?.nextSibling) {
            this.wrapper.insertBefore(spinner, form.nextSibling);
        } else {
            this.wrapper.appendChild(spinner);
        }

        return spinner;
    }

    /**
     * Initializes the search component. Exits early if required elements or globals are missing.
     */
    init() {
        if (!this.input || !this.dropdown) return;
        if (typeof npVars === 'undefined' || !npVars.ajax_url || !npVars.nonce) return;

        this.setupAria();
        this.addLiveRegion();
        this.addEvents();
    }

    /**
     * Configures ARIA attributes for the combobox pattern.
     */
    setupAria() {
        const listboxId = this.dropdown.id || `${this.instId}-listbox`;
        if (!this.dropdown.id) this.dropdown.id = listboxId;

        this.input.setAttribute('role', 'combobox');
        this.input.setAttribute('aria-expanded', 'false');
        this.input.setAttribute('aria-controls', listboxId);
        this.input.setAttribute('aria-autocomplete', 'list');
        this.input.setAttribute('aria-label', this.input.getAttribute('aria-label') || 'Search pages in this section');
    }

    /**
     * Adds a visually hidden live region for screen reader announcements.
     */
    addLiveRegion() {
        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.liveRegion.className = 'visually-hidden';
        this.liveRegion.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
        this.wrapper.appendChild(this.liveRegion);
    }

    /**
     * Announces a message to screen readers.
     * @param {string} message
     */
    announce(message) {
        if (!this.liveRegion) return;
        this.liveRegion.textContent = '';
        setTimeout(() => { this.liveRegion.textContent = message; }, 100);
    }

    /**
     * Attaches event listeners for input, focus, keydown, click-outside, and focusout.
     */
    addEvents() {
        const debouncedSearch = this.debounce((event) => this.search(event.target.value), 300);

        this.input.addEventListener('input', debouncedSearch);
        this.input.addEventListener('focus', () => {
            if (this.lastResults?.length && this.input.value.length >= 2) {
                this.showDropdown(this.lastResults);
            }
        });

        this.wrapper.addEventListener('keydown', (e) => this.handleKeydown(e));
        document.addEventListener('click', (e) => {
            if (!this.wrapper.contains(e.target)) this.hideDropdown();
        });

        this.wrapper.addEventListener('focusout', () => {
            requestAnimationFrame(() => {
                if (!this.wrapper.contains(document.activeElement)) this.hideDropdown();
            });
        });
    }

    /** @returns {HTMLElement[]} Focusable result links in the dropdown */
    getFocusableItems() {
        return Array.from(this.dropdown.querySelectorAll('.page-archive-search-result'));
    }

    /** @returns {boolean} */
    isDropdownOpen() {
        return !this.dropdown.classList.contains('d-none');
    }

    /**
     * Handles keyboard navigation (Escape, Tab, Arrow keys, Enter).
     * @param {KeyboardEvent} e
     */
    handleKeydown(e) {
        const onInput = e.target === this.input;

        if (e.key === 'Escape') {
            if (this.isDropdownOpen()) {
                e.preventDefault();
                this.hideDropdown();
                this.input.focus();
            }
            return;
        }

        if (e.key === 'Tab') {
            this.handleTab(e, onInput);
            return;
        }

        if (!onInput) return;

        const items = this.dropdown.querySelectorAll('[role="option"]');

        if (!items.length) {
            if (e.key === 'Enter' && this.input.value.length > 0 && this.input.value.length < 2) {
                e.preventDefault();
                this.announce('Type at least 2 characters to search');
            }

            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
            this.highlightItem(items);

            return;
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
            this.highlightItem(items);

            return;
        }

        if (e.key === 'Enter' && this.selectedIndex >= 0 && items[this.selectedIndex]) {
            e.preventDefault();
            window.location.href = items[this.selectedIndex].href;
        }
    }

    /**
     * Handles Tab/Shift+Tab for focus trap between input and result links.
     * @param {KeyboardEvent} e
     * @param {boolean} onInput
     */
    handleTab(e, onInput) {
        if (!this.isDropdownOpen()) return;

        const items = this.getFocusableItems();
        if (!items.length) return;

        if (onInput) {
            if (e.shiftKey) {
                this.hideDropdown();
                return;
            }
            e.preventDefault();
            items[0].focus();
            return;
        }

        const currentIndex = items.indexOf(document.activeElement);
        if (currentIndex === -1) return;

        if (e.shiftKey) {
            e.preventDefault();
            if (currentIndex === 0) this.input.focus();
            else items[currentIndex - 1].focus();

            return;
        }

        e.preventDefault();
        if (currentIndex === items.length - 1) this.input.focus();
        else items[currentIndex + 1].focus();
    }

    /**
     * Performs the AJAX search and updates the dropdown.
     * @param {string} term
     */
    async search(term) {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        if (!term || term.length < 2) {
            this.hideDropdown();
            if (term?.length === 1) this.announce('Type at least 2 characters to search');
            return;
        }

        this.setBusy(true);
        this.announce('Searching…');

        try {
            const response = await fetch(npVars.ajax_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'page_archive_search',
                    nonce: npVars.nonce,
                    s: term,
                    parent_category: this.parentCategory,
                }),
                signal: this.abortController.signal,
            }); 

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            const results = Array.isArray(data) ? data : [];

            this.lastResults = results;
            this.showDropdown(results);
        } catch (error) {
            if (error.name === 'AbortError') return;
            console.error('Page Archive Search error:', error);
            this.showError();
        } finally {
            this.setBusy(false);
        }
    }

    /**
     * Sets loading state (spinner visibility, aria-busy).
     * @param {boolean} busy
     */
    setBusy(busy) {
        this.input.setAttribute('aria-busy', busy ? 'true' : 'false');

        if (this.spinner) {
            this.spinner.classList.toggle('is-loading', busy);
            this.spinner.setAttribute('aria-hidden', busy ? 'false' : 'true');
        }
    }

    /**
     * Renders the error state using the error template or a fallback DOM fragment.
     */
    showError() {
        this.hideDropdown();
        this.announce('Search unavailable. Please try again.');

        if (this.tplError) {
            this.dropdown.appendChild(this.tplError.content.cloneNode(true));
        } else {
            const el = document.createElement('div');
            el.className = 'p-3 text-danger small';
            el.setAttribute('role', 'alert');
            el.textContent = 'Search unavailable. Please try again.';
            this.dropdown.appendChild(el);
        }

        this.dropdown.classList.remove('d-none');
        this.dropdown.setAttribute('aria-hidden', 'false');
    }

    /**
     * Renders search results or the no-results state.
     * @param {{url: string, title: string}[]} results
     */
    showDropdown(results) {
        this.selectedIndex = -1;
        this.dropdown.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'true');
        this.input.removeAttribute('aria-activedescendant');

        if (!results.length) {
            if (this.tplNoResults) {
                this.dropdown.appendChild(this.tplNoResults.content.cloneNode(true));
            } else {
                const el = document.createElement('div');
                el.className = 'p-3 text-secondary small';
                el.setAttribute('role', 'status');
                el.textContent = 'No results found';
                this.dropdown.appendChild(el);
            }

            this.dropdown.setAttribute('role', 'listbox');
            this.dropdown.setAttribute('aria-label', 'Search results');
            this.dropdown.classList.remove('d-none');
            this.dropdown.setAttribute('aria-hidden', 'false');
            this.announce('No results found');

            return;
        }

        this.dropdown.setAttribute('role', 'listbox');
        this.dropdown.setAttribute('aria-label', 'Search results');

        results.forEach((item, i) => {
            const node = this.tplResult.content.cloneNode(true).firstElementChild;
            node.href = item.url;
            node.id = `${this.instId}-opt-${i}`;
            node.dataset.index = i;

            const titleEl = node.querySelector('[data-title]') || node.querySelector('span');
            if (titleEl) titleEl.textContent = item.title;

            node.addEventListener('click', (e) => {
                e.preventDefault();
                window.location.href = item.url;
            });

            this.dropdown.appendChild(node);
        });

        this.dropdown.classList.remove('d-none');
        this.dropdown.setAttribute('aria-hidden', 'false');
        this.announce(`${results.length} result${results.length === 1 ? '' : 's'} found`);
    }

    /** Hides and clears the dropdown. */
    hideDropdown() {
        this.dropdown.classList.add('d-none');
        this.dropdown.setAttribute('aria-hidden', 'true');
        this.dropdown.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
        this.input.removeAttribute('aria-busy');
        this.selectedIndex = -1;
    }

    /**
     * Updates visual highlight and aria-activedescendant for arrow-key navigation.
     * @param {NodeListOf<Element>} items
     */
    highlightItem(items) {
        items.forEach((el, i) => {
            el.classList.toggle('bg-light', i === this.selectedIndex);
        });

        if (this.selectedIndex >= 0) {
            const opt = items[this.selectedIndex];
            this.input.setAttribute('aria-activedescendant', opt.id);
            opt.scrollIntoView({ block: 'nearest' });
            this.announce(opt.textContent);
        } else {
            this.input.removeAttribute('aria-activedescendant');
        }
    }

    /**
     * Debounces a function.
     * @param {(...args: unknown[]) => void} func
     * @param {number} wait
     * @returns {(...args: unknown[]) => void}
     */
    debounce(func, wait) {
        let timeout;
        
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }
}

if (document.readyState === 'interactive' || document.readyState === 'complete') {
    PageArchiveSearch.setupModules();
} else {
    document.addEventListener('DOMContentLoaded', PageArchiveSearch.setupModules);
}
